import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

// --- Morgan 4/4 Series II — 1958 (the atelier's founding automobile) ---
const MORGAN_ASSETS = {
  hero: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/hwov4wed_P1000576.webp",   // 3/4 rear, urban context
  gallery: [
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/4tmat5ex_P1000566.webp", // 3/4 front high
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/70l8o4yf_P1000563.webp", // 3/4 front street
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/hwov4wed_P1000576.webp", // 3/4 rear urban
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/p0ysfsxr_P1000565.webp", // front close-up
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/jle4h07e_P1000578.webp", // headlights & grille sunset
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/bikcitbu_P1000600.webp", // cockpit steering
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/m98i44ca_P1000610.webp", // Morgan gauge macro
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/wvywha0z_P1000570.webp", // Morgan winged badge
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/waw2eaee_P1000588.webp", // spare wheel rack detail
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/pmun8fxy_P1000571.webp", // bonnet strap detail
    "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/rizwtbh1_P1000584.webp", // leather strap macro
  ],
};

export const FLEET_CARS = [
  {
    id: "morgan-4-4",
    i18nKey: "ds21",
    cover: MORGAN_ASSETS.hero,
    gallery: MORGAN_ASSETS.gallery,
    specs: {
      it: [
        { label: "Anno", value: "1958" },
        { label: "Modello", value: "Series II" },
        { label: "Motore", value: "Ford 100E sidevalve, 4 cilindri, 1.172 cc" },
        { label: "Potenza", value: "36 CV" },
        { label: "Coppia", value: "72,5 Nm" },
        { label: "Cambio", value: "Manuale 3 rapporti" },
        { label: "Velocità max", value: "120 km/h" },
        { label: "Peso", value: "~680 kg" },
        { label: "Telaio", value: "Frassino europeo, pannelli in alluminio" },
        { label: "Origine", value: "Malvern, Inghilterra" },
      ],
      en: [
        { label: "Year", value: "1958" },
        { label: "Model", value: "Series II" },
        { label: "Engine", value: "Ford 100E side-valve, 4 cyl, 1,172 cc" },
        { label: "Power", value: "36 HP" },
        { label: "Torque", value: "72.5 Nm" },
        { label: "Gearbox", value: "3-speed manual" },
        { label: "Top speed", value: "120 km/h" },
        { label: "Weight", value: "~680 kg" },
        { label: "Body", value: "European ash frame, aluminium panels" },
        { label: "Origin", value: "Malvern, England" },
      ],
    },
  },
];

// --- On Request partner cars (sourced via the ETERNA On Request division) ---
export const PARTNER_CARS = [
  {
    id: "porsche-356-speedster",
    i18nKey: "porsche356",
    onRequest: true,
    partner: true,
    cover: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/f49cc0d8ae4e6372_4F95C3F8-296B-4D2C-9E02-39E433684F0C-IMG_6501.jpeg",
    gallery: [
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/f49cc0d8ae4e6372_4F95C3F8-296B-4D2C-9E02-39E433684F0C-IMG_6501.jpeg",
      { src: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/6d068473d2ea547a_60F51966-7B5B-41E2-9094-A6DB5E940C6D-DSC_7599%20copia.jpeg", fit: "contain" },
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/b1e78c547b74daa6_E95DADF3-C930-48F8-B406-6A43CB60AD64-ffaf3a6f-c86a-44aa-b5d5-9ebc9f21bbb3.jpeg",
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/7c7b48f033f14df1_221E44E7-FB79-4D90-81CE-763B0ACF7B53-IMG_6676.jpeg",
      { src: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/e899dd011c66b245_A87DED8B-4346-484C-AC2D-90AD3373EEAD-952daa2a-8d4f-4b01-ab7f-e593541a4a5b.jpeg", fit: "contain" },
    ],
    specs: {
      it: [
        { label: "Anno", value: "1958" },
        { label: "Modello", value: "356 A 1600 Speedster" },
        { label: "Motore", value: "Boxer 4 cilindri, 1.582 cc, raffreddamento ad aria" },
        { label: "Potenza", value: "60 CV (75 CV Super)" },
        { label: "Cambio", value: "Manuale 4 rapporti" },
        { label: "Velocità max", value: "~160 km/h" },
        { label: "Peso", value: "~760 kg" },
        { label: "Carrozzeria", value: "Speedster 2 posti, parabrezza basso" },
        { label: "Origine", value: "Zuffenhausen, Germania" },
      ],
      en: [
        { label: "Year", value: "1958" },
        { label: "Model", value: "356 A 1600 Speedster" },
        { label: "Engine", value: "Air-cooled flat-4, 1,582 cc" },
        { label: "Power", value: "60 HP (75 HP Super)" },
        { label: "Gearbox", value: "4-speed manual" },
        { label: "Top speed", value: "~160 km/h" },
        { label: "Weight", value: "~760 kg" },
        { label: "Body", value: "2-seat Speedster, low windscreen" },
        { label: "Origin", value: "Zuffenhausen, Germany" },
      ],
    },
  },
  {
    id: "mercedes-190-sl",
    i18nKey: "mb190sl",
    onRequest: true,
    partner: true,
    cover: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/d1b952fd6c795f32_A5C7BF0C-1715-4E1A-AC21-58BEE7EC7504-DSC_7637.jpeg",
    gallery: [
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/d1b952fd6c795f32_A5C7BF0C-1715-4E1A-AC21-58BEE7EC7504-DSC_7637.jpeg",
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/eb1445800a429426_ADC7CC83-8A16-43CF-BF2D-6E777B01BA58-f18c3804-6ac5-49cf-8876-8bd2f3b70552.jpeg",
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/3725812b2f207081_C75DB68E-DFF6-4136-84B9-78F72BDE4377-DSC_7649%20copia.jpeg",
      { src: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/67427cf5ec6b2d78_7A50BC3E-00F2-471C-9073-59DC97DD434F-9a161cb2-32b8-4438-be3f-030fa47ae213.jpeg", fit: "contain" },
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/068932ff2875d0d5_59C1AB85-2186-4847-BD31-5DA71CAD296E-DSC_7626%20copia.jpeg",
    ],
    specs: {
      it: [
        { label: "Anno", value: "1959" },
        { label: "Modello", value: "190 SL (W121)" },
        { label: "Motore", value: "4 cilindri in linea, 1.897 cc" },
        { label: "Potenza", value: "105 CV" },
        { label: "Cambio", value: "Manuale 4 rapporti" },
        { label: "Velocità max", value: "~171 km/h" },
        { label: "Peso", value: "~1.160 kg" },
        { label: "Carrozzeria", value: "Roadster 2 posti, hardtop opzionale" },
        { label: "Origine", value: "Stoccarda, Germania" },
      ],
      en: [
        { label: "Year", value: "1959" },
        { label: "Model", value: "190 SL (W121)" },
        { label: "Engine", value: "Inline-4, 1,897 cc" },
        { label: "Power", value: "105 HP" },
        { label: "Gearbox", value: "4-speed manual" },
        { label: "Top speed", value: "~171 km/h" },
        { label: "Weight", value: "~1,160 kg" },
        { label: "Body", value: "2-seat roadster, optional hardtop" },
        { label: "Origin", value: "Stuttgart, Germany" },
      ],
    },
  },
  {
    id: "mercedes-230-sl",
    i18nKey: "mb230sl",
    onRequest: true,
    partner: true,
    cover: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/492818984b1fc898_43D62C6E-8EB1-410D-8E64-C3686389C5EF-Marco%20-%20230SL-84.jpeg",
    gallery: [
      { src: "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/492818984b1fc898_43D62C6E-8EB1-410D-8E64-C3686389C5EF-Marco%20-%20230SL-84.jpeg", fit: "contain" },
      "https://static.prod-images.emergentagent.com/jobs/ca71243b-4320-4e92-bee7-6ea7e28c997a/images/8f76ff74e752e2c027aa008f561599c142d3ad47ce46e5212a323f09ad0bbc21.jpeg",
      "https://static.prod-images.emergentagent.com/jobs/ca71243b-4320-4e92-bee7-6ea7e28c997a/images/2e4861505ea41c809ad6ee687cec771a613d1b19ff235f58f3ebffcc2f45f667.jpeg",
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/2448a150c2dfc4d5_06590BAE-DDC4-43B9-8AF6-4E02DD957411-Marco%20-%20230SL-72.jpeg",
      "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/abf2945d45869931_58981663-041F-4D72-B36E-0D091046E2CC-Immagine%2013.jpeg",
    ],
    specs: {
      it: [
        { label: "Anno", value: "1965" },
        { label: "Modello", value: "230 SL «Pagoda» (W113)" },
        { label: "Motore", value: "6 cilindri in linea, 2.306 cc, iniezione" },
        { label: "Potenza", value: "150 CV" },
        { label: "Cambio", value: "Manuale 4 rapporti" },
        { label: "Velocità max", value: "~200 km/h" },
        { label: "Peso", value: "~1.300 kg" },
        { label: "Carrozzeria", value: "Roadster con hardtop «a pagoda»" },
        { label: "Origine", value: "Stoccarda, Germania" },
      ],
      en: [
        { label: "Year", value: "1965" },
        { label: "Model", value: "230 SL \u201CPagoda\u201D (W113)" },
        { label: "Engine", value: "Inline-6, 2,306 cc, fuel injection" },
        { label: "Power", value: "150 HP" },
        { label: "Gearbox", value: "4-speed manual" },
        { label: "Top speed", value: "~200 km/h" },
        { label: "Weight", value: "~1,300 kg" },
        { label: "Body", value: "Roadster with \u201Cpagoda\u201D hardtop" },
        { label: "Origin", value: "Stuttgart, Germany" },
      ],
    },
  },
];

// Teaser card — generic "future icons" placeholder, no specific make/model
export const COMING_SOON_CARS = [
  {
    id: "future-icon-placeholder",
    i18nKey: "futurePlaceholder",
    cover: MORGAN_ASSETS.gallery[4], // atmospheric detail as evocative teaser
    year: "",
    specs: { it: [], en: [] },
    isPlaceholder: true,
  },
];

export default function FleetCarDialog({ car, open, onOpenChange }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [car?.id, open]);
  const { t, i18n } = useTranslation();
  if (!car) return null;
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const k = car.i18nKey;

  const prev = () =>
    setIdx((v) => (v - 1 + car.gallery.length) % car.gallery.length);
  const next = () => setIdx((v) => (v + 1) % car.gallery.length);

  const story = t(`fleet.${k}.story`, { returnObjects: true }) || [];

  const galItem = car.gallery[idx];
  const galSrc = typeof galItem === "string" ? galItem : galItem.src;
  const galFit = typeof galItem === "object" && galItem.fit ? galItem.fit : "cover";
  const galPos = typeof galItem === "object" && galItem.pos ? galItem.pos : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`car-dialog-${car.id}`}
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 border-0 rounded-none bg-[#FAF9F6] [&>button]:hidden"
      >
        <DialogTitle className="sr-only">{t(`fleet.${k}.name`)}</DialogTitle>
        <DialogDescription className="sr-only">{t(`fleet.${k}.tagline`)}</DialogDescription>

        <div className="relative w-full h-[40vh] md:h-[55vh] overflow-hidden bg-[#1A1B1A]">
          <AnimatePresence mode="wait">
            <motion.img
              key={galSrc}
              src={galSrc}
              alt={`${t(`fleet.${k}.name`)} - ${idx + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className={`w-full h-full ${galFit === "contain" ? "object-contain" : "object-cover"}`}
              style={galPos ? { objectPosition: galPos } : undefined}
              data-testid={`car-dialog-image-${idx}`}
            />
          </AnimatePresence>

          {car.gallery.length > 1 && (
            <>
              <button type="button" onClick={prev} data-testid="car-dialog-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#FAF9F6]/90 hover:bg-[#C4A462] hover:text-[#FAF9F6] text-[#1A1B1A] transition-colors duration-300"
                aria-label="prev">
                <ChevronLeft strokeWidth={1.25} size={20} />
              </button>
              <button type="button" onClick={next} data-testid="car-dialog-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#FAF9F6]/90 hover:bg-[#C4A462] hover:text-[#FAF9F6] text-[#1A1B1A] transition-colors duration-300"
                aria-label="next">
                <ChevronRight strokeWidth={1.25} size={20} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {car.gallery.map((item, i) => (
                  <button type="button" key={typeof item === "string" ? item : item.src} onClick={() => setIdx(i)} data-testid={`car-dialog-dot-${i}`}
                    className={`h-[2px] transition-all duration-300 ${i === idx ? "w-8 bg-[#C4A462]" : "w-4 bg-[#FAF9F6]/60"}`}
                    aria-label={`go to ${i + 1}`} />
                ))}
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="close"
            data-testid="car-dialog-close"
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-[#FAF9F6]/90 hover:bg-[#C4A462] hover:text-[#FAF9F6] text-[#1A1B1A] transition-colors duration-300"
          >
            <X strokeWidth={1.25} size={20} />
          </button>
        </div>

        <div className="p-8 md:p-12 lg:p-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              {car.onRequest && (
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="car-dialog-badges">
                  <span className="inline-block bg-[#1A1B1A] text-[#C4A462] px-3 py-1 text-[10px] uppercase tracking-[0.25em]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {t("fleet.on_request_badge")}
                  </span>
                  {car.partner && (
                    <span className="inline-block border border-[#C4A462] text-[#C4A462] px-3 py-1 text-[10px] uppercase tracking-[0.25em]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                      {t("fleet.partner_badge")}
                    </span>
                  )}
                </div>
              )}
              <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t(`fleet.${k}.year`)}
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
                {t(`fleet.${k}.name`)}
              </h2>
              <p className="text-lg italic text-[#4A4A4A] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
                {t(`fleet.${k}.tagline`)}
              </p>
              {Array.isArray(story) && story.map((para) => (
                <p key={para} className="text-sm text-[#4A4A4A] leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  {para}
                </p>
              ))}
              {car.onRequest && (
                <div className="mt-6 border-l-2 border-[#C4A462] pl-5 py-1" data-testid="car-dialog-onrequest-note">
                  <p className="text-sm text-[#4A4A4A] leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: "1.05rem" }}>
                    {t("fleet.on_request_note")}
                  </p>
                </div>
              )}
            </div>

            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#757575] mb-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                {t("fleet.specs_label")}
              </p>
              <dl className="space-y-3">
                {car.specs[lang].map((spec) => (
                  <div key={spec.label} className="flex items-baseline justify-between gap-4 pb-2 border-b border-[#D8D4CC]/50">
                    <dt className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>
                      {spec.label}
                    </dt>
                    <dd className="text-sm text-[#1A1B1A] text-right" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>
                      {spec.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
